import { useEffect, useMemo, useState, useRef, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  copiarCampoGrid,
  eliminarFilaGrid,
  estadoInicial,
  resetModal,
  setModal
} from 'slices/modalSlice'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { LoginContext } from '../context/loginContext'
import { Buscador } from '../reutilizables/buscador/Buscador.jsx'
import { GenericModal } from '../reutilizables/modal/GenericModal.jsx'
import { Paginacion } from '../reutilizables/paginacion/Paginacion.jsx'
import {
  actualizarLista,
  buscarListaPorVendedor,
  eliminarLista,
  insertarLista
} from '../servicios/listaEnvioService.js'
import BordePrincipal from '../ui/BordePrincipal.jsx'
import { ItemLista } from './ItemLista'
import styles from './Lista.module.css'

const campos = [
  {
    type: 'text',
    name: 'nombreLista',
    placeholder: 'Ingrese el nombre la lista',
    label: 'Nombre de la lista',
    isFullWidth: true,
    isEditable: false,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaDesde',
    label: 'Comienzo de la vigencia',
    isFullWidth: false,
    isEditable: false,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaHasta',
    label: 'Fin de la vigencia',
    isFullWidth: false,
    isEditable: true,
    isGrid: false
  },
  {
    type: 'text',
    name: 'zona',
    placeholder: 'Zona del envio',
    label: 'Zona',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'cantidadDesde',
    label: 'Cantidad desde',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'cantidadHasta',
    label: 'Cantidad hasta',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'costo',
    label: 'Costo (sin IVA)',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'tEstimado',
    label: 'Dias entrega',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'select',
    name: 'moneda',
    placeholder: 'Seleccione una',
    label: 'Moneda',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: true,
    options: [
      { value: 'ARS', label: 'ARS ($)', key: 1 },
      { value: 'USD', label: 'USD (U$S)', key: 2 },
      { value: 'EUR', label: 'EUR (€)', key: 3 }
    ]
  }
]

const PAGINA_ITEMS = 15

export const Lista = () => {
  const [paginaActual, setPaginaActual] = useState(1)
  const [itemsPorPagina, setItemsPorPagina] = useState(PAGINA_ITEMS)
  const [lista, setLista] = useState([])
  const [listaActual, setListaActual] = useState([])
  const topRef = useRef(null)
  const [listaAEliminar, setListaAEliminar] = useState(null)
  const [actualizar, setActualizar] = useState(false)
  const [ordenNombre, setOrdenNombre] = useState(null)
  const prevIdUsuarioRef = useRef(null)
  const [busqueda, setBusqueda] = useState('')
  const [terminoBusqueda, setTerminoBusqueda] = useState('')
  const [ordenVencimiento, setOrdenVencimiento] = useState(false)

  const dispatch = useDispatch()
  const { loginDataContext } = useContext(LoginContext)

  const { editMode, values, errores, type } = useSelector((state) => state.genericModal)

  const idUsuarioActual = loginDataContext?.idUsuario
  const idVendedor = idUsuarioActual || 0

  useEffect(() => {
    dispatch(setModal({ campos: campos }))
  }, [])

  useEffect(() => {
    if (!prevIdUsuarioRef.current && idUsuarioActual) {
      prevIdUsuarioRef.current = idUsuarioActual
      dispatch(setModal({ values: { idVendedor: idUsuarioActual } }))
    } else if (prevIdUsuarioRef.current && prevIdUsuarioRef.current !== idUsuarioActual) {
      dispatch(resetModal())
      dispatch(
        setModal({
          campos: campos,
          values: { idVendedor: idUsuarioActual }
        })
      )
      prevIdUsuarioRef.current = idUsuarioActual
      setLista([])
      setActualizar((prev) => !prev)
    }
  }, [idUsuarioActual, dispatch])

  const handleChange = (e) => {
    const { name, value, filaIndex = '' } = e.target
    const campo = filaIndex !== '' ? `${name}_${filaIndex}` : name
    const nuevasValidaciones = { ...errores }

    if (filaIndex === '') {
      dispatch(setModal({ values: { ...values, [name]: value } }))
    } else {
      const copia = [...values.grilla]
      copia[filaIndex] = { ...copia[filaIndex], [name]: value }
      dispatch(setModal({ values: { grilla: copia } }))
    }

    if (errores[campo]) {
      delete nuevasValidaciones[campo]
    }
    dispatch(setModal({ errores: nuevasValidaciones }))
  }

  const formatearFecha = (fecha) => {
    if (!fecha) {
      return ''
    }
    return fecha.split('T')[0]
  }

  const editarLista = (item) => {
    const filas = (item.grilla || [])
      .sort((a, b) => a.zona.toLowerCase().localeCompare(b.zona.toLowerCase()))
      .map((fila, idx) => ({
        zona: fila.zona ?? '',
        cantidadDesde: Number(fila.cantidadDesde ?? 0),
        cantidadHasta: Number(fila.cantidadHasta ?? 0),
        costo: Number(fila.costo ?? 0),
        moneda: fila.moneda ?? '',
        tEstimado: Number(fila.tEstimado ?? 0),
        posicion: fila.posicion ?? idx + 1
      }))

    dispatch(
      setModal({
        size: 'lg',
        title: 'Editar lista',
        type: 'form',
        body: '',
        show: true,
        editMode: true,
        values: {
          idLista: item.idLista,
          idVendedor: item.idVendedor,
          nombreLista: item.nombreLista,
          vigenciaDesde: formatearFecha(item.vigenciaDesde),
          vigenciaHasta: formatearFecha(item.vigenciaHasta),
          vigenciaHastaOriginal: formatearFecha(item.vigenciaHasta),
          zona: item.zona,
          grilla: filas.length ? filas : estadoInicial.grilla
        }
      })
    )
  }

  const eliminarListaPagina = (item) => {
    dispatch(
      setModal({
        size: 'sm',
        title: 'Eliminar lista',
        type: 'botones',
        body: '¿Estás seguro que deseas eliminar la lista?',
        show: true
      })
    )
    setListaAEliminar(item)
  }

  const abrirModal = () => {
    dispatch(
      setModal({
        size: 'lg',
        title: 'Crear lista',
        type: 'form',
        body: '',
        show: true,
        editMode: false,
        values: { ...values, idVendedor: idVendedor }
      })
    )
  }

  const handleConfirmEliminar = async () => {
    if (!listaAEliminar) {
      return
    }
    try {
      await eliminarLista(listaAEliminar.idLista, listaAEliminar.idVendedor)
      setListaAEliminar(null)
      setActualizar((prev) => !prev)
      return true
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar la lista.`
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  const handleConfirmModal = async () => {
    const errores = {}

    const { nombreLista, vigenciaDesde, vigenciaHasta } = values

    if ((!nombreLista || nombreLista.trim() === '') && !editMode) {
      errores.nombreLista = 'El nombre es obligatorio.'
    }

    if (!vigenciaDesde && !editMode) {
      errores.vigenciaDesde = 'Debe seleccionar una fecha de comienzo de vigencia.'
    } else if (!editMode) {
      const fechaDesdeStr = vigenciaDesde
      const fechaActualStr = new Date().toISOString().split('T')[0]

      if (fechaDesdeStr < fechaActualStr) {
        errores.vigenciaDesde =
          'La fecha de inicio debe ser igual o posterior a la fecha actual.'
      }
    }

    if (!vigenciaHasta) {
      errores.vigenciaHasta = 'La fecha de fin de vigencia es obligatoria.'
    } else if (vigenciaHasta <= vigenciaDesde) {
      errores.vigenciaHasta =
        'La fecha de fin de vigencia debe ser mayor que la de comienzo.'
    } else if (editMode && vigenciaHasta < values.vigenciaHastaOriginal) {
      errores.vigenciaHasta =
        'No puede establecer una fecha anterior a la vigencia original.'
    }

    values.grilla.forEach((fila, i) => {
      const { cantidadDesde, cantidadHasta, costo, moneda, tEstimado, zona } = fila

      if (!zona || zona.trim() === '') {
        errores[`zona_${i}`] = 'La zona es obligatoria.'
      }

      if (cantidadDesde < 0 || !cantidadDesde) {
        errores[`cantidadDesde_${i}`] = 'Debe ingresar una cantidad mínima.'
      }

      if (i > 0) {
        const zonaActual = fila.zona?.trim().toUpperCase()
        const desdeActual = Number(cantidadDesde)

        const filasMismaZona = values.grilla
          .filter((f, idx) => idx < i && f.zona?.trim().toUpperCase() === zonaActual)
          .sort((a, b) => Number(a.cantidadDesde) - Number(b.cantidadDesde))

        if (filasMismaZona.length > 0) {
          const filaAnterior = filasMismaZona[filasMismaZona.length - 1]
          const hastaAnterior = Number(filaAnterior.cantidadHasta)

          if (desdeActual !== hastaAnterior + 1) {
            errores[`cantidadDesde_${i}`] =
              `En la zona "${fila.zona}", la cantidad desde debe ser ${hastaAnterior + 1} para evitar brechas.`
          }
        }
      }

      const desde = Number(cantidadDesde)
      const hasta = Number(cantidadHasta)

      if (!hasta || hasta <= desde) {
        errores[`cantidadHasta_${i}`] = 'La cantidad máxima debe ser mayor que la mínima.'
      }

      if (costo <= 0 || !costo) {
        errores[`costo_${i}`] = 'Debe ingresar el valor del costo.'
      }

      if (!moneda) {
        errores[`moneda_${i}`] = 'Debe seleccionar una moneda.'
      }

      if (tEstimado <= 0 || !tEstimado) {
        errores[`tEstimado_${i}`] = 'Debe ingresar el tiempo estimado.'
      }
    })

    if (
      !editMode &&
      lista.some(
        (l) =>
          l.nombreLista.trim().toLowerCase() === values.nombreLista.trim().toLowerCase()
      )
    ) {
      errores.nombreLista = 'Ya existe una lista con este nombre.'
    }

    dispatch(
      setModal({
        errores: errores,
        type: 'form'
      })
    )

    if (Object.keys(errores).length > 0) {
      return false
    }
    try {
      if (editMode) {
        await handleActualizar()
      } else {
        await handleConfirmarAgregado()
      }
      return true
    } catch {
      return false
    }
  }

  const handleActualizar = async () => {
    try {
      const listasAEnviar = values.grilla.map((fila, index) => ({
        nombreLista: values.nombreLista,
        vigenciaDesde: values.vigenciaDesde,
        vigenciaHasta: values.vigenciaHasta,
        idVendedor: values.idVendedor,
        grilla: [
          {
            zona: fila.zona,
            cantidadDesde: Number(fila.cantidadDesde),
            cantidadHasta: Number(fila.cantidadHasta),
            costo: Number(fila.costo),
            moneda: fila.moneda,
            tEstimado: Number(fila.tEstimado),
            posicion: index + 1
          }
        ]
      }))

      await actualizarLista(listasAEnviar)
      dispatch(setModal({ show: false }))
      setActualizar((prev) => !prev)
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar la lista.`
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  const handleConfirmarAgregado = async () => {
    const nuevaLista = {
      nombreLista: values.nombreLista,
      vigenciaDesde: values.vigenciaDesde,
      vigenciaHasta: values.vigenciaHasta,
      idVendedor: values.idVendedor,
      grilla: []
    }

    values.grilla.forEach((fila) => {
      nuevaLista.grilla.push({
        zona: fila.zona,
        cantidadDesde: fila.cantidadDesde,
        cantidadHasta: fila.cantidadHasta,
        costo: fila.costo,
        moneda: fila.moneda,
        tEstimado: fila.tEstimado
      })
    })
    try {
      await insertarLista(nuevaLista)
      setActualizar((prev) => !prev)
      dispatch(setModal({ show: false }))
    } catch (error) {
      const mensaje = `Ocurrió un error al agregar la lista.`
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  const traerInfo = async () => {
    try {
      if (!idVendedor) {
        return
      }
      const { data } = await buscarListaPorVendedor(idVendedor)
      const unificadas = Object.values(
        data.reduce((acc, item) => {
          const key = item.nombreLista.trim().toLowerCase()

          if (!acc[key]) {
            acc[key] = {
              idLista: item.idLista,
              nombreLista: item.nombreLista,
              vigenciaDesde: item.vigenciaDesde,
              vigenciaHasta: item.vigenciaHasta,
              idVendedor: item.idVendedor,
              grilla: []
            }
          }

          if (Array.isArray(item.grilla)) {
            acc[key].grilla.push(...item.grilla)
          }

          return acc
        }, {})
      ).map((l) => ({
        ...l,
        contador: l.grilla.length
      }))
      setLista(unificadas)
    } catch (error) {
      const mensaje = `Ocurrió un error al cargar las listas.`
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  useEffect(() => {
    traerInfo()
  }, [actualizar, idVendedor])

  const ordenarLista = (lista, orden, porVencimiento) => {
    if (porVencimiento) {
      return [...lista].sort((a, b) => {
        const fechaA = new Date(a.vigenciaHasta)
        const fechaB = new Date(b.vigenciaHasta)
        return fechaA - fechaB
      })
    }

    if (!orden) {
      return lista
    }
    return [...lista].sort((a, b) => {
      if (a.nombreLista.toLowerCase() < b.nombreLista.toLowerCase()) {
        return orden === 'asc' ? -1 : 1
      }

      if (a.nombreLista.toLowerCase() > b.nombreLista.toLowerCase()) {
        return orden === 'asc' ? 1 : -1
      }
      return 0
    })
  }

  const listaFiltradaOrdenada = useMemo(() => {
    let listaFiltrada = lista
    if (terminoBusqueda) {
      listaFiltrada = lista.filter((item) =>
        item.nombreLista.toLowerCase().includes(terminoBusqueda)
      )
    }
    return ordenarLista(listaFiltrada, ordenNombre, ordenVencimiento)
  }, [lista, terminoBusqueda, ordenNombre, ordenVencimiento])

  const totalPaginas = Math.ceil(listaFiltradaOrdenada.length / itemsPorPagina)

  useEffect(() => {
    const indiceUltimoItem = paginaActual * itemsPorPagina
    const indicePrimerItem = indiceUltimoItem - itemsPorPagina
    setListaActual(listaFiltradaOrdenada.slice(indicePrimerItem, indiceUltimoItem))
  }, [listaFiltradaOrdenada, paginaActual, itemsPorPagina])

  const agregarFila = () => {
    const nuevaFila = {
      zona: '',
      cantidadDesde: 0,
      cantidadHasta: 0,
      costo: 0,
      moneda: '',
      tEstimado: 0
    }

    dispatch(
      setModal({
        values: {
          grilla: [...(values.grilla || []), nuevaFila]
        }
      })
    )
  }

  const eliminarFila = (index) => {
    dispatch(eliminarFilaGrid(index))
  }

  const copiarCampo = (name, value) => {
    dispatch(copiarCampoGrid({ name, value }))
  }

  const handleBusqueda = (valor) => {
    setTerminoBusqueda(valor)
    setPaginaActual(1)
  }

  const handleOrdenarPorVencimiento = () => {
    setOrdenVencimiento(true)
    setOrdenNombre(null)
    setPaginaActual(1)
  }

  const handleOrdenarAZ = (orden) => {
    setOrdenNombre(orden)
    setOrdenVencimiento(false)
    setPaginaActual(1)
  }

  return (
    <div className={styles.listaContainer} ref={topRef}>
      <BordePrincipal titulo="Mis Listas">
        <>
          <div className="container">
            <div className="row">
              <div className={`${styles.ordenamientoContainer} mb-3`}>
                <div className="col-6">
                  <Buscador
                    busqueda={busqueda}
                    setBusqueda={setBusqueda}
                    onChange={handleBusqueda}
                    placeholder="Buscar lista..."
                  />
                </div>
                <button
                  onClick={() => handleOrdenarAZ('asc')}
                  className={`${styles.btn} ${styles.btnOutlinePrimary}`}
                >
                  A-Z
                </button>
                <button
                  onClick={() => handleOrdenarAZ('desc')}
                  className={`${styles.btn} ${styles.btnOutlinePrimary}`}
                >
                  Z-A
                </button>
                <button
                  onClick={handleOrdenarPorVencimiento}
                  className={`${styles.btn} ${styles.btnOutlinePrimary}`}
                >
                  Próximos vencimientos
                </button>
                <button
                  onClick={abrirModal}
                  className={`${styles.btn} ${styles.btnAgregarLista}`}
                >
                  Agregar Lista
                </button>
              </div>
              {listaActual.map((item, index) => (
                <div className="col-12 col-md-4 mb-3" key={item.idLista ?? index}>
                  <ItemLista
                    lista={item}
                    onEditar={() => editarLista(item)}
                    onEliminar={() => eliminarListaPagina(item)}
                  />
                </div>
              ))}
              <GenericModal
                onConfirm={type === 'form' ? handleConfirmModal : handleConfirmEliminar}
                onChange={handleChange}
                onAddItem={agregarFila}
                onRemoveItem={eliminarFila}
                onCopyField={copiarCampo}
              />
            </div>
          </div>

          {totalPaginas > 0 && (
            <Paginacion
              totalPaginas={totalPaginas}
              paginaActual={paginaActual}
              onCambioPagina={(num) => {
                setPaginaActual(num)
                topRef.current?.scrollIntoView({ behavior: 'smooth' })
              }}
              itemsPorPagina={itemsPorPagina}
              onCambioItemsPorPagina={(num) => {
                setItemsPorPagina(num)
                setPaginaActual(1)
                topRef.current?.scrollIntoView({ behavior: 'smooth' })
              }}
            />
          )}
        </>
      </BordePrincipal>
    </div>
  )
}
